import { useCallback, useMemo } from 'react';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { formatInTimeZone as formatTz, fromZonedTime, getTimezoneOffset, toZonedTime } from 'date-fns-tz';

import { useAuthQuery } from '../auth/hooks/useAuthQuery';

interface UseTimezoneHook {
  formatInTimezone: (date?: Date | string, dateFormat?: string) => string;
  getDateInTimezone: (date?: Date | string) => Date;
  getTimeOfDay: (date?: Date | string) => string;
  mergeDateTime: (date?: Date | string, time?: string) => Date;
  getStartOfDate: (date: Date) => Date;
  getEndOfDate: (date: Date) => Date;
}

const isValidDate = (date?: string | Date) => {
  if (!date) {
    return false;
  }

  return typeof date === 'string' || (date instanceof Date && !!date.getDate());
};

export const useTimezone = (): UseTimezoneHook => {
  const { user } = useAuthQuery();
  const userTimezone = user?.timeZoneName || '';

  const formatInTimezone = useCallback(
    (date: Date | string = '', dateFormat?: string): string => {
      if (!isValidDate(date) || !dateFormat) {
        return '';
      }

      return formatTz(date, userTimezone, dateFormat);
    },
    [userTimezone]
  );

  const getDateInTimezone = useCallback(
    (date: Date | string = '') => {
      return isValidDate(date) ? toZonedTime(date, userTimezone) : toZonedTime(new Date(), userTimezone);
    },
    [userTimezone]
  );

  const getTimeOfDay = useCallback(
    (date: Date | string = ''): string => {
      if (!isValidDate(date)) {
        return '';
      }
      date = typeof date === 'string' ? parseISO(date) : date;
      const zonedTime = getDateInTimezone(date);
      const hours = zonedTime.getHours();

      if (hours < 12) {
        return 'Morning';
      } else if (hours >= 12 && hours <= 17) {
        return 'Afternoon';
      }

      return 'Evening';
    },
    [getDateInTimezone]
  );

  const mergeDateTime = useCallback(
    (date: Date | string = '', time?: string): Date => {
      if (!isValidDate(date) || !time) {
        return getDateInTimezone();
      }
      date = typeof date === 'string' ? parseISO(date) : date;
      const dateOnly = format(date, 'yyyy-MM-dd');

      return fromZonedTime(`${dateOnly}T${time}`, userTimezone);
    },
    [getDateInTimezone, userTimezone]
  );

  const getStartOfDate = useCallback(
    (date: Date): Date => {
      if (!isValidDate(date)) {
        return new Date();
      }
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      date = startOfDay(date);
      // use date as param for Daylight Saving locations
      const tzOffset = getTimezoneOffset(userTimezone, date) - getTimezoneOffset(browserTz, date);

      return new Date(date.valueOf() - tzOffset);
    },
    [userTimezone]
  );

  const getEndOfDate = useCallback(
    (date: Date): Date => {
      if (!isValidDate(date)) {
        return new Date();
      }
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      date = endOfDay(date);
      // use date as param for Daylight Saving locations
      const tzOffset = getTimezoneOffset(userTimezone, date) - getTimezoneOffset(browserTz, date);

      return new Date(date.valueOf() - tzOffset);
    },
    [userTimezone]
  );

  return useMemo(
    () => ({ formatInTimezone, getDateInTimezone, getTimeOfDay, mergeDateTime, getStartOfDate, getEndOfDate }),
    [formatInTimezone, getDateInTimezone, getTimeOfDay, mergeDateTime, getStartOfDate, getEndOfDate]
  );
};
